import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import NewsletterSubscribe from './NewsletterSubscribe';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const buildContactHref = (item) => {
  const val = (item.value || '').trim();
  if (!val) return null;
  switch (item.type) {
    case 'phone':
      return `tel:${val.replace(/\s+/g, '')}`;
    case 'email':
      return `mailto:${val}`;
    case 'whatsapp': {
      const digits = val.replace(/\D+/g, '');
      return `https://wa.me/${digits}`;
    }
    default:
      return null;
  }
};

const Footer = () => {
  const [contactItems, setContactItems] = useState([]);
  const [visitLocations, setVisitLocations] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/site-content`)
      .then(res => {
        if (Array.isArray(res.data?.contact_us_items)) {
          setContactItems(res.data.contact_us_items);
        } else {
          // Legacy fallback from old flat contact_phone / contact_email keys
          const fallback = [];
          if (res.data?.contact_phone) fallback.push({ id: 'legacy-phone', type: 'phone', value: res.data.contact_phone, hidden: false });
          if (res.data?.contact_email) fallback.push({ id: 'legacy-email', type: 'email', value: res.data.contact_email, hidden: false });
          setContactItems(fallback);
        }
        if (Array.isArray(res.data?.visit_us_locations)) {
          setVisitLocations(res.data.visit_us_locations);
        }
      })
      .catch(() => { /* silent fallback */ });
  }, []);

  const visibleContacts = contactItems.filter(c => !c.hidden && (c.value || '').trim());

  // Group visible locations by group_heading, preserving array order.
  const visibleLocations = visitLocations.filter(loc => !loc.hidden);
  const groupedLocations = [];
  visibleLocations.forEach(loc => {
    const heading = loc.group_heading || '';
    const last = groupedLocations[groupedLocations.length - 1];
    if (last && last.heading === heading) {
      last.items.push(loc);
    } else {
      groupedLocations.push({ heading, items: [loc] });
    }
  });

  const showRightColumn = visibleContacts.length > 0 || groupedLocations.length > 0;

  return (
    <footer className="bg-[#0A2342] text-white py-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Weekly Ocean Report opt-in */}
        <div className="mb-10 pb-10 border-b border-white/10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="text-[#FF6B35] text-xs uppercase tracking-widest font-semibold mb-2">Weekly Ocean Report</div>
              <h3 className="text-xl md:text-2xl font-bold text-white">Salt in your inbox every Monday.</h3>
              <p className="text-gray-400 text-sm mt-1">Stories, reef news, gear we use, and trip drops — straight from the dive team.</p>
            </div>
            <NewsletterSubscribe variant="inline" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo and About */}
          <div className="md:col-span-1">
            <img 
              src="https://customer-assets.emergentagent.com/job_scubastore-eft/artifacts/171jpjea_JustDive.co.za%20LOGO%20LIGHT.png" 
              alt="Just Dive Logo" 
              className="h-16 w-auto mb-4"
            />
            <p className="text-gray-400 text-sm">
              Your gateway to underwater adventures. Quality gear, expert training, unforgettable trips.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-4">Shop</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/products?category=featured" className="hover:text-[#FF6B35] transition-colors">Featured Gear</Link></li>
              <li><Link to="/products" className="hover:text-[#FF6B35] transition-colors">New Gear</Link></li>
              <li><Link to="/pre-owned" className="hover:text-[#FF6B35] transition-colors">Pre-Dived Gear</Link></li>
              <li><Link to="/membership" className="hover:text-[#FF6B35] transition-colors">Memberships</Link></li>
              <li><Link to="/courses" className="hover:text-[#FF6B35] transition-colors">Courses</Link></li>
              <li><Link to="/trips" className="hover:text-[#FF6B35] transition-colors">Dive Trips</Link></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-semibold text-lg mb-4">Support</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/faq" className="hover:text-[#FF6B35] transition-colors">FAQ</Link></li>
              <li><Link to="/terms" className="hover:text-[#FF6B35] transition-colors">Terms & Conditions</Link></li>
            </ul>
            
            <h4 className="font-semibold text-lg mb-4 mt-6">Forms & Documents</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/medical-questionnaire" className="hover:text-[#FF6B35] transition-colors">Medical Questionnaire</Link></li>
              <li><Link to="/liability-waiver" className="hover:text-[#FF6B35] transition-colors">Liability Waiver</Link></li>
            </ul>
          </div>

          {/* Rightmost column: Contact Us (top) + Visit Us (below) */}
          {showRightColumn && (
            <div>
              {visibleContacts.length > 0 && (
                <>
                  <h4 className="font-semibold text-lg mb-4">Contact Us</h4>
                  <ul className="space-y-2 text-sm text-gray-400 mb-6" data-testid="footer-contact-us">
                    {visibleContacts.map(item => {
                      const href = buildContactHref(item);
                      const isWa = item.type === 'whatsapp';
                      const content = (
                        <>
                          {item.label && <span className="text-gray-500 mr-1">{item.label}:</span>}
                          <span>{item.value}</span>
                        </>
                      );
                      return (
                        <li key={item.id} data-testid={`footer-contact-${item.id}`}>
                          {href ? (
                            <a
                              href={href}
                              target={isWa ? '_blank' : undefined}
                              rel={isWa ? 'noopener noreferrer' : undefined}
                              className="hover:text-[#FF6B35] transition-colors break-all"
                            >
                              {content}
                            </a>
                          ) : content}
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}

              {groupedLocations.length > 0 && (
                <>
                  <h4 className="font-semibold text-lg mb-4">Visit Us</h4>
                  <div className="space-y-4 text-sm text-gray-400" data-testid="footer-visit-us">
                    {groupedLocations.map((group, gi) => (
                      <div key={`grp-${gi}`}>
                        {group.heading && (
                          <p className="text-xs uppercase tracking-wider text-[#FF6B35] font-semibold mb-2">
                            {group.heading}
                          </p>
                        )}
                        <div className="space-y-3">
                          {group.items.map(loc => (
                            <div key={loc.id} data-testid={`footer-location-${loc.id}`}>
                              {loc.name && <p className="font-medium text-white">{loc.name}</p>}
                              {(loc.address || '').split('\n').map((line, li) => (
                                line.trim() ? <p key={li}>{line}</p> : null
                              ))}
                              {loc.phone && (
                                <p className="text-[#FF6B35]">
                                  <a
                                    href={`tel:${loc.phone.replace(/\s+/g, '')}`}
                                    className="hover:underline"
                                  >
                                    {loc.phone}
                                  </a>
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-gray-700 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm text-gray-400">
            © {new Date().getFullYear()} Just Dive. All rights reserved.
          </p>
          <div className="flex gap-4 mt-4 md:mt-0">
            <Link to="/terms" className="text-sm text-gray-400 hover:text-[#FF6B35] transition-colors">Terms</Link>
            <Link to="/faq" className="text-sm text-gray-400 hover:text-[#FF6B35] transition-colors">FAQ</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
